import type { ComponentType } from 'react'
import type { SvgIconProps } from '@mui/material'
import {
  Dashboard as DashboardIcon,
  Category as CollectionIcon,
  Storage as StorageIcon,
  Book as BookIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Public as DatabaseIcon,
  HelpOutline as HelpIcon,
  Favorite as WishlistIcon,
  Inventory2 as BoxesIcon,
  ReportProblem as ReportIssueIcon,
  BarChart as AnalyticsIcon,
  Tune as PreferencesIcon,
  Settings as SettingsIcon,
  NotificationsNone as NotificationsIcon,
  UploadFile as ImportIcon,
  CreditCard as SubscriptionIcon,
  Home as HomeIcon,
  Search as FindIcon,
  MoreHoriz as MoreIcon,
  LocalFireDepartment as SmokeIcon,
} from '@mui/icons-material'
import { isNavItemActive } from './sidebarUtils'

export type NavItemDef = {
  text: string
  Icon: ComponentType<SvgIconProps>
  path: string
  /** Stable id for analytics (`nav_more_item.item`) */
  analyticsId?: string
}

export const primaryItems: NavItemDef[] = [
  { text: 'Dashboard', Icon: DashboardIcon, path: '/app/dashboard', analyticsId: 'dashboard' },
  { text: 'Collection', Icon: CollectionIcon, path: '/app/collection', analyticsId: 'collection' },
  { text: 'Wishlist', Icon: WishlistIcon, path: '/app/wishlist', analyticsId: 'wishlist' },
  { text: 'Humidors', Icon: StorageIcon, path: '/app/humidors', analyticsId: 'humidors' },
  { text: 'Boxes', Icon: BoxesIcon, path: '/app/boxes', analyticsId: 'boxes' },
  { text: 'Smoking Journal', Icon: BookIcon, path: '/app/journal', analyticsId: 'journal' },
  { text: 'History', Icon: HistoryIcon, path: '/app/history', analyticsId: 'history' },
]

export const insightItems: NavItemDef[] = [
  {
    text: 'Session Analytics',
    Icon: AnalyticsIcon,
    path: '/app/analytics',
    analyticsId: 'analytics',
  },
  {
    text: 'Flavor Preferences',
    Icon: PreferencesIcon,
    path: '/app/preferences',
    analyticsId: 'preferences',
  },
]

export const accountItems: NavItemDef[] = [
  { text: 'Profile', Icon: PersonIcon, path: '/app/profile', analyticsId: 'profile' },
  { text: 'Settings', Icon: SettingsIcon, path: '/app/settings', analyticsId: 'settings' },
  {
    text: 'Notifications',
    Icon: NotificationsIcon,
    path: '/app/settings/notifications',
    analyticsId: 'notifications',
  },
  {
    text: 'Subscription',
    Icon: SubscriptionIcon,
    path: '/app/settings/subscription',
    analyticsId: 'subscription',
  },
  { text: 'Import Collection', Icon: ImportIcon, path: '/app/import', analyticsId: 'import' },
]

export const utilityItems: NavItemDef[] = [
  { text: 'Cigar Database', Icon: DatabaseIcon, path: '/app/database', analyticsId: 'database' },
  { text: 'Report Issue', Icon: ReportIssueIcon, path: '/app/feedback', analyticsId: 'feedback' },
  { text: 'Help Center', Icon: HelpIcon, path: '/app/help', analyticsId: 'help' },
]

export const adminEntryItem: NavItemDef = {
  text: 'Admin Panel',
  Icon: AdminIcon,
  path: '/admin/dashboard',
  analyticsId: 'admin',
}

export type BottomNavTabId = 'home' | 'collection' | 'smoke' | 'find' | 'more'

export type BottomTabDef = {
  id: BottomNavTabId
  label: string
  Icon: ComponentType<SvgIconProps>
  path?: string
}

export const bottomTabs: BottomTabDef[] = [
  { id: 'home', label: 'Home', Icon: HomeIcon, path: '/app/dashboard' },
  { id: 'collection', label: 'Collection', Icon: CollectionIcon, path: '/app/collection' },
  { id: 'smoke', label: 'Smoke', Icon: SmokeIcon, path: '/app/journal' },
  { id: 'find', label: 'Find', Icon: FindIcon, path: '/app/database' },
  { id: 'more', label: 'More', Icon: MoreIcon },
]

const SUBSCRIPTION_PATH = '/app/settings/subscription'

/** Account group with Subscription removed in beta (shared by Sidebar and More). */
export function getVisibleAccountItems(beta: boolean): NavItemDef[] {
  return beta ? accountItems.filter((item) => item.path !== SUBSCRIPTION_PATH) : accountItems
}

const MORE_SHEET_LEAD_PATHS = [
  '/app/humidors',
  '/app/boxes',
  '/app/wishlist',
  '/app/history',
  '/app/analytics',
  '/app/preferences',
] as const

const MORE_SHEET_TRAIL_PATHS = ['/app/help', '/app/feedback'] as const

function itemsByPath(groups: NavItemDef[][]): Map<string, NavItemDef> {
  return new Map(groups.flat().map((item) => [item.path, item]))
}

function pickNavItems(lookup: Map<string, NavItemDef>, paths: readonly string[]): NavItemDef[] {
  return paths.flatMap((path) => {
    const item = lookup.get(path)
    return item ? [item] : []
  })
}

/** Items listed in the More sheet (order matches product IA). */
export function getMoreNavItems(options: { beta: boolean; isAdmin: boolean }): NavItemDef[] {
  const { beta, isAdmin } = options
  const lookup = itemsByPath([primaryItems, insightItems, utilityItems])

  const items: NavItemDef[] = [
    ...pickNavItems(lookup, MORE_SHEET_LEAD_PATHS),
    ...getVisibleAccountItems(beta),
    ...pickNavItems(lookup, MORE_SHEET_TRAIL_PATHS),
  ]

  if (isAdmin) {
    items.push(adminEntryItem)
  }

  return items
}

export function bottomTabPath(tabId: BottomNavTabId): string | undefined {
  return bottomTabs.find((t) => t.id === tabId)?.path
}

export function resolveBottomNavTab(pathname: string): BottomNavTabId {
  if (isNavItemActive('/app/dashboard', pathname)) {
    return 'home'
  }
  if (isNavItemActive('/app/collection', pathname)) {
    return 'collection'
  }
  if (isNavItemActive('/app/journal', pathname)) {
    return 'smoke'
  }
  if (isNavItemActive('/app/database', pathname)) {
    return 'find'
  }
  return 'more'
}

export type BottomTabPressResult =
  | { action: 'open-more' }
  | { action: 'none' }
  | { action: 'navigate'; target: string; replace: boolean }

/**
 * Tab-bar press intent. Rapid repeats of the same destination (before the
 * route updates) are ignored so the same tab is not stacked in history.
 */
export function resolveBottomTabPress(
  pathname: string,
  tabId: BottomNavTabId,
  pendingTarget: string | null = null,
): BottomTabPressResult {
  if (tabId === 'more') {
    return { action: 'open-more' }
  }

  const target = bottomTabPath(tabId)
  if (!target) {
    return { action: 'none' }
  }

  const alreadyOnTab = resolveBottomNavTab(pathname) === tabId
  if (alreadyOnTab && pathname === target) {
    return { action: 'none' }
  }

  if (pendingTarget === target && !alreadyOnTab) {
    return { action: 'none' }
  }

  return { action: 'navigate', target, replace: alreadyOnTab }
}
